import type { TechId } from './ids';
/**
 * Diplomacy, raids, invasion and vassalage (GDD §8). War serves the
 * economy: raids take stock without razing anything, and a beaten capital
 * becomes a vassal that keeps playing. Starting values for tuning.
 */
export const AGREEMENT_IDS = ['trade', 'nonAggression', 'alliance'] as const;
export type AgreementId = (typeof AGREEMENT_IDS)[number];

export const DIPLOMACY = {
  /** A declared war starts after this many ticks; everyone sees it coming (GDD §8: 30 s). */
  warDelayTicks: 300,
  /** Reputation every player starts with, 0..100. */
  reputationStart: 50,
  /** Reputation lost for a peacetime raid (Reforged only) and for breaking a non-aggression pact. */
  peacetimeRaidPenalty: 10,
  brokenPactPenalty: 10,
} as const;

/**
 * The trade agreement (#74, author's decision 20.10.2026): bonuses to the
 * caravans between the partners while it is in force, and a trade route that
 * grows with every delivery between them. Starting values for tuning.
 */
export const TRADE_AGREEMENT = {
  /** Share added to the speed of caravans between the partners (stacks with technologies). */
  caravanSpeed: 0.25,
  /** Trade income per delivery between the partners, × this, for both sides. */
  incomeFactor: 1.5,
  /** Deliveries between the pair that reach route levels 2 and 3 (level 1 from the start). */
  routeLevels: [5, 15],
  /** Level 2: a caravan on the route holds this share more. */
  cargoBonus: 0.25,
  /** Level 3: armoured caravans — robbers take at most this share of the cargo. */
  armoredLootShare: 0.5,
} as const;

export const RAID = {
  /** The raiding fleet must hold the target this long (GDD §8: 20 s). */
  holdTicks: 200,
  /** Share of each stockpiled good a raid can take, before the cargo limit. */
  stockShare: 0.2,
  /** Cargo a warship carries off on a raid, on top of real cargo holds. */
  cargoPerArmedShip: 10,
  /** Reforged raid loot multiplier (GDD §4: +50%). */
  reforgedLoot: 1.5,
  /** A raided outpost hands over this many cycles of output and then produces nothing... */
  outpostLootCycles: 2,
  /** ...for this many cycles. */
  outpostDownCycles: 3,
} as const;

export const INVASION = {
  /** Technology that allows landings and sieges (#27). */
  requiresTech: 'orbitalAssault' as TechId,
  /** Troops each lander puts down (#135). */
  troopsPerLander: 40,
  /** Militia every capital has without a garrison. */
  baseGarrison: 10,
  /** Each point of shield capacity counts this much toward the garrison. */
  shieldToGarrison: 0.1,
  /** Siege before the capital falls (GDD §8: 90 s). */
  siegeTicks: 900,
  /** Share of the vassal's taxes paid to its overlord each cycle (GDD §8: 25%). */
  tribute: 0.25,
  /**
   * Overlord's armed strength in the vassal's capital hex is the sum of its
   * ships' damage per round × this; a garrison above it may rebel.
   */
  fleetStrengthPerDamage: 1,
  /** A vassal may rebel this long after its overlord failed a crisis (#29). */
  crisisFailureWindowTicks: 3000,
} as const;
